import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Image,
  Spinner,
  Badge,
  Tooltip,
  IconButton,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Alert,
  AlertIcon,
  Wrap,
  WrapItem,
  Flex,
  Collapse,
  Divider,
  Code,
  SimpleGrid,
} from '@chakra-ui/react';
import {
  FaPlay,
  FaPause,
  FaStepBackward,
  FaStepForward,
  FaArrowLeft,
  FaSignOutAlt,
  FaCloud,
  FaSyncAlt,
  FaChevronDown,
  FaChevronUp,
  FaInfoCircle,
  FaDownload,
} from 'react-icons/fa';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/api';
import Footer from './Footer';
import NavDropdowns from './NavDropdowns';

const PRELOAD_AHEAD = 50;
const PRELOAD_BEHIND = 20;

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const formatDuration = (seconds) => {
  if (!seconds || seconds === 0) return '0m';
  if (seconds < 60) return '<1m';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

const FPS_OPTIONS = [5, 10, 20, 30];

const SimulationViewer = () => {
  const { jobId } = useParams();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [manifest, setManifest] = useState(null);
  const [sessionToken, setSessionToken] = useState(null);
  const [jobDetails, setJobDetails] = useState(null);
  const [caseCode, setCaseCode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  const [selectedField, setSelectedField] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [fps, setFps] = useState(10);
  const [buffering, setBuffering] = useState(false);

  const preloadedFrames = useRef(new Set());
  const preloadQueue = useRef(new Set());
  const playIntervalRef = useRef(null);
  const containerRef = useRef(null);

  // --- Fetch manifest + session token + job details ---
  const fetchManifest = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [manifestRes, sessionRes] = await Promise.all([
        fetch(`${apiService.baseURL}/job/${jobId}/manifest`, {
          headers: apiService.getHeaders(true),
        }),
        fetch(`${apiService.baseURL}/get-render-session/${jobId}`, {
          headers: apiService.getHeaders(true),
        }),
      ]);

      if (!manifestRes.ok) {
        if (manifestRes.status === 404) {
          throw new Error('Renders not available yet. The job may still be processing.');
        }
        throw new Error(`Failed to load manifest (HTTP ${manifestRes.status})`);
      }
      if (!sessionRes.ok) {
        throw new Error(`Failed to get render session (HTTP ${sessionRes.status})`);
      }

      const [data, session] = await Promise.all([
        manifestRes.json(),
        sessionRes.json(),
      ]);

      setManifest(data);
      setSessionToken(session.session_token);
      const fields = Object.keys(data.fields || {});
      if (fields.length > 0) setSelectedField(fields[0]);
      setCurrentIndex(0);

      // Fetch job details + case code (non-blocking)
      apiService.getUserJobs()
        .then(jobs => {
          const job = (jobs || []).find(j => j.job_id === jobId);
          if (job) setJobDetails(job);
        })
        .catch(() => {});

      fetch(`${apiService.baseURL}/job-case/${jobId}`, {
        headers: apiService.getHeaders(true),
      })
        .then(res => res.ok ? res.json() : null)
        .then(data => {
          if (data?.exists && data.case_content) setCaseCode(data.case_content);
        })
        .catch(() => {});
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => { fetchManifest(); }, [fetchManifest]);

  // --- Image URL builder (public, no auth needed) ---
  const buildImageUrl = useCallback((field, timestepIndex) => {
    if (!manifest || !sessionToken) return null;
    const ts = manifest.timesteps[timestepIndex];
    if (!ts || !ts.fields[field]) return null;
    const p = ts.fields[field].path;
    const filename = p.split('/')[1];
    return `${apiService.baseURL}/render/${sessionToken}/${field}/${filename}`;
  }, [manifest, sessionToken]);

  // --- Image preloading (simple Image() — no auth headers needed) ---
  const preloadImage = useCallback((field, index) => {
    const cacheKey = `${field}:${index}`;
    if (preloadedFrames.current.has(cacheKey) || preloadQueue.current.has(cacheKey)) return;

    const url = buildImageUrl(field, index);
    if (!url) return;

    preloadQueue.current.add(cacheKey);
    const img = new window.Image();
    img.onload = () => {
      preloadedFrames.current.add(cacheKey);
      preloadQueue.current.delete(cacheKey);
    };
    img.onerror = () => {
      preloadQueue.current.delete(cacheKey);
    };
    img.src = url;
  }, [buildImageUrl]);

  const preloadWindow = useCallback((field, centerIndex) => {
    if (!manifest || !sessionToken) return;
    const total = manifest.timesteps.length;

    // Evict far-behind and far-ahead entries
    for (const key of preloadedFrames.current) {
      const [f, idxStr] = key.split(':');
      const idx = parseInt(idxStr, 10);
      if (f !== field || idx < centerIndex - PRELOAD_BEHIND * 2 || idx > centerIndex + PRELOAD_AHEAD * 2) {
        preloadedFrames.current.delete(key);
      }
    }

    for (let i = 0; i < PRELOAD_AHEAD && centerIndex + i < total; i++) {
      preloadImage(field, centerIndex + i);
    }
    for (let i = 1; i <= PRELOAD_BEHIND && centerIndex - i >= 0; i++) {
      preloadImage(field, centerIndex - i);
    }
  }, [manifest, sessionToken, preloadImage]);

  // Trigger preload on field/index change
  useEffect(() => {
    if (selectedField && manifest && sessionToken) {
      preloadWindow(selectedField, currentIndex);
    }
  }, [selectedField, currentIndex, manifest, sessionToken, preloadWindow]);

  // --- Check if current timestep has data for selected field ---
  const hasDataForCurrentFrame = manifest && selectedField &&
    manifest.timesteps[currentIndex] &&
    manifest.timesteps[currentIndex].fields[selectedField];

  // --- Current image URL (direct <img> src, no fetch needed) ---
  const currentImageUrl = hasDataForCurrentFrame ? buildImageUrl(selectedField, currentIndex) : null;

  // --- Playback ---
  useEffect(() => {
    if (!playing || !manifest) return;

    const total = manifest.timesteps.length;
    if (total <= 1) { setPlaying(false); return; }

    playIntervalRef.current = setInterval(() => {
      setCurrentIndex(prev => {
        const next = prev + 1;
        if (next >= total) {
          setPlaying(false);
          return prev;
        }
        const nextKey = `${selectedField}:${next}`;
        if (!preloadedFrames.current.has(nextKey)) {
          setBuffering(true);
          return prev;
        }
        setBuffering(false);
        return next;
      });
    }, 1000 / fps);

    return () => clearInterval(playIntervalRef.current);
  }, [playing, fps, manifest, selectedField]);

  // When buffering, keep polling until the next frame is preloaded
  useEffect(() => {
    if (!buffering || !playing) return;
    const check = setInterval(() => {
      setCurrentIndex(prev => {
        const nextKey = `${selectedField}:${prev + 1}`;
        if (preloadedFrames.current.has(nextKey)) {
          setBuffering(false);
          return prev + 1;
        }
        return prev;
      });
    }, 100);
    return () => clearInterval(check);
  }, [buffering, playing, selectedField]);

  // --- Keyboard controls ---
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!manifest || manifest.timesteps.length <= 1) return;
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

      const total = manifest.timesteps.length;
      switch (e.key) {
        case ' ':
          e.preventDefault();
          setPlaying(p => !p);
          break;
        case 'ArrowRight':
          e.preventDefault();
          setPlaying(false);
          setCurrentIndex(prev => Math.min(prev + 1, total - 1));
          break;
        case 'ArrowLeft':
          e.preventDefault();
          setPlaying(false);
          setCurrentIndex(prev => Math.max(prev - 1, 0));
          break;
        default:
          break;
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [manifest]);

  // --- Field change handler ---
  const handleFieldChange = (field) => {
    setSelectedField(field);
    setPlaying(false);
    setBuffering(false);
  };

  // --- Derived values ---
  const totalSteps = manifest ? manifest.timesteps.length : 0;
  const currentStep = manifest && manifest.timesteps[currentIndex]
    ? manifest.timesteps[currentIndex].step : 0;
  const maxStep = manifest && manifest.timesteps.length > 0
    ? manifest.timesteps[manifest.timesteps.length - 1].step : 0;
  const fieldInfo = manifest && selectedField ? manifest.fields[selectedField] : null;
  const sim = manifest ? manifest.simulation : null;
  const singleFrame = totalSteps <= 1;

  // --- Render ---

  if (loading) {
    return (
      <Box minH="100vh" bg="gray.50" display="flex" alignItems="center" justifyContent="center">
        <VStack spacing={4}>
          <Spinner size="xl" color="brand.500" thickness="4px" />
          <Text color="gray.600">Loading simulation viewer...</Text>
        </VStack>
      </Box>
    );
  }

  if (error) {
    return (
      <Box minH="100vh" bg="gray.50">
        <HeaderBar user={user} logout={logout} navigate={navigate} />
        <Container maxW="container.lg" py={20}>
          <VStack spacing={6}>
            <Alert status="warning" borderRadius="lg" maxW="lg">
              <AlertIcon />
              <Box>
                <Text fontWeight="bold">Renders Not Available</Text>
                <Text fontSize="sm">{error}</Text>
              </Box>
            </Alert>
            <HStack spacing={3}>
              <Button leftIcon={<FaSyncAlt />} colorScheme="brand" onClick={fetchManifest}>
                Retry
              </Button>
              <Button leftIcon={<FaArrowLeft />} variant="outline" onClick={() => navigate('/jobs')}>
                Back to Jobs
              </Button>
            </HStack>
          </VStack>
        </Container>
      </Box>
    );
  }

  if (!manifest) return null;

  const fieldKeys = Object.keys(manifest.fields);

  return (
    <Box minH="100vh" bg="gray.50">
      <HeaderBar user={user} logout={logout} navigate={navigate} />

      <Container maxW="container.xl" py={6}>
        <VStack spacing={5} align="stretch">

          {/* Job Details Panel */}
          <Box bg="white" borderRadius="lg" border="1px" borderColor="gray.200" shadow="sm" overflow="hidden">
            <HStack
              px={4} py={3}
              cursor="pointer"
              onClick={() => setShowDetails(d => !d)}
              _hover={{ bg: 'gray.50' }}
              transition="background 0.15s"
              justify="space-between"
            >
              <HStack spacing={2}>
                <IconButton
                  icon={<FaArrowLeft />}
                  variant="ghost"
                  size="xs"
                  onClick={(e) => { e.stopPropagation(); navigate('/jobs'); }}
                  aria-label="Back to jobs"
                />
                <FaInfoCircle color="var(--chakra-colors-gray-400)" size={14} />
                <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                  {jobDetails?.job_name || `Job ${jobId}`}
                </Text>
              </HStack>
              {showDetails ? <FaChevronUp color="var(--chakra-colors-gray-400)" size={12} /> : <FaChevronDown color="var(--chakra-colors-gray-400)" size={12} />}
            </HStack>

            <Collapse in={showDetails} animateOpacity>
              <Divider />
              <Box px={4} py={4}>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                  {/* Left column */}
                  <VStack spacing={2} align="stretch">
                    <HStack spacing={2}>
                      <Text fontSize="xs" color="gray.500" fontWeight="semibold" minW="80px">Job ID</Text>
                      <Code fontSize="xs" colorScheme="gray" px={2} py={0.5} borderRadius="md">{jobId}</Code>
                    </HStack>

                    {jobDetails?.job_name && (
                      <HStack spacing={2}>
                        <Text fontSize="xs" color="gray.500" fontWeight="semibold" minW="80px">Name</Text>
                        <Text fontSize="xs" color="gray.700">{jobDetails.job_name}</Text>
                      </HStack>
                    )}

                    {jobDetails?.job_desc && (
                      <HStack spacing={2} align="start">
                        <Text fontSize="xs" color="gray.500" fontWeight="semibold" minW="80px">Description</Text>
                        <Text fontSize="xs" color="gray.700">{jobDetails.job_desc}</Text>
                      </HStack>
                    )}

                    {jobDetails?.status && (
                      <HStack spacing={2}>
                        <Text fontSize="xs" color="gray.500" fontWeight="semibold" minW="80px">Status</Text>
                        <Badge colorScheme="green" fontSize="2xs">{jobDetails.status}</Badge>
                      </HStack>
                    )}

                    {jobDetails?.tool_name && (
                      <HStack spacing={2}>
                        <Text fontSize="xs" color="gray.500" fontWeight="semibold" minW="80px">Tool</Text>
                        <Text fontSize="xs" color="gray.700">{jobDetails.tool_name}</Text>
                      </HStack>
                    )}
                  </VStack>

                  {/* Right column */}
                  <VStack spacing={2} align="stretch">
                    {jobDetails?.enqueued_at && (
                      <HStack spacing={2}>
                        <Text fontSize="xs" color="gray.500" fontWeight="semibold" minW="80px">Submitted</Text>
                        <Text fontSize="xs" color="gray.700" fontFamily="mono">{formatDate(jobDetails.enqueued_at)}</Text>
                      </HStack>
                    )}
                    {jobDetails?.started_at && (
                      <HStack spacing={2}>
                        <Text fontSize="xs" color="gray.500" fontWeight="semibold" minW="80px">Started</Text>
                        <Text fontSize="xs" color="gray.700" fontFamily="mono">{formatDate(jobDetails.started_at)}</Text>
                      </HStack>
                    )}
                    {jobDetails?.ended_at && (
                      <HStack spacing={2}>
                        <Text fontSize="xs" color="gray.500" fontWeight="semibold" minW="80px">Ended</Text>
                        <Text fontSize="xs" color="gray.700" fontFamily="mono">{formatDate(jobDetails.ended_at)}</Text>
                      </HStack>
                    )}
                    {jobDetails?.total_duration > 0 && (
                      <HStack spacing={2}>
                        <Text fontSize="xs" color="gray.500" fontWeight="semibold" minW="80px">Total Time</Text>
                        <Badge colorScheme="blue" fontSize="2xs" fontFamily="mono">{formatDuration(jobDetails.total_duration)}</Badge>
                      </HStack>
                    )}
                    {jobDetails?.processing_duration > 0 && (
                      <HStack spacing={2}>
                        <Text fontSize="xs" color="gray.500" fontWeight="semibold" minW="80px">Processing</Text>
                        <Badge colorScheme="purple" fontSize="2xs" fontFamily="mono">{formatDuration(jobDetails.processing_duration)}</Badge>
                      </HStack>
                    )}
                  </VStack>
                </SimpleGrid>

                {caseCode && (
                  <Box mt={2}>
                    <Divider mb={3} />
                    <Box borderRadius="lg" overflow="hidden" shadow="md">
                      <HStack
                        justify="space-between"
                        bg="#282c34"
                        px={4}
                        py={2}
                        borderBottom="1px solid"
                        borderColor="whiteAlpha.100"
                      >
                        <HStack spacing={2}>
                          <HStack spacing={1.5}>
                            <Box w={3} h={3} borderRadius="full" bg="red.400" />
                            <Box w={3} h={3} borderRadius="full" bg="yellow.400" />
                            <Box w={3} h={3} borderRadius="full" bg="green.400" />
                          </HStack>
                          <Text color="gray.500" fontFamily="mono" fontSize="xs" ml={2}>pyfr.ini</Text>
                        </HStack>
                        <Button
                          size="xs"
                          variant="ghost"
                          color="gray.400"
                          _hover={{ color: 'white', bg: 'whiteAlpha.200' }}
                          leftIcon={<FaDownload />}
                          onClick={() => {
                            const blob = new Blob([caseCode], { type: 'text/x-python' });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = 'pyfr.ini';
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                            URL.revokeObjectURL(url);
                          }}
                        >
                          Download
                        </Button>
                      </HStack>
                      <Box bg="#1e1e2e" maxH="400px" overflowY="auto" overflowX="auto">
                        <HStack spacing={0} align="stretch" minW="fit-content">
                        <Box
                          py={4} pl={3} pr={3}
                          bg="rgba(0,0,0,0.2)"
                          borderRight="1px solid"
                          borderColor="whiteAlpha.100"
                          userSelect="none" textAlign="right"
                          minW="48px"
                          position="sticky" left={0}
                          zIndex={1}
                        >
                          {caseCode.split('\n').map((_, i) => (
                            <Text key={i} fontSize="0.78rem" color="#636d83" fontFamily="'JetBrains Mono', 'Fira Code', monospace" lineHeight="21px">{i + 1}</Text>
                          ))}
                        </Box>
                        <Box flex={1}>
                          <SyntaxHighlighter
                            style={oneDark}
                            language="python"
                            showLineNumbers={false}
                            customStyle={{
                              backgroundColor: 'transparent',
                              padding: '1rem',
                              fontSize: '0.78rem',
                              fontFamily: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace",
                              lineHeight: '21px',
                              margin: 0,
                            }}
                            codeTagProps={{
                              style: {
                                fontFamily: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace",
                                lineHeight: '21px',
                                fontSize: '0.78rem',
                              },
                            }}
                          >
                            {caseCode}
                          </SyntaxHighlighter>
                        </Box>
                      </HStack>
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
            </Collapse>
          </Box>

          {/* Top section: Field selector + Metadata */}
          <Flex
            direction={{ base: 'column', md: 'row' }}
            justify="space-between"
            align={{ base: 'stretch', md: 'center' }}
            gap={4}
          >
            {/* Field buttons */}
            <Wrap spacing={2}>
              {fieldKeys.map(field => (
                <WrapItem key={field}>
                  <Button
                    size="sm"
                    variant={selectedField === field ? 'solid' : 'outline'}
                    colorScheme={selectedField === field ? 'brand' : 'gray'}
                    onClick={() => handleFieldChange(field)}
                    fontFamily="mono"
                    fontSize="xs"
                    textTransform="none"
                  >
                    {field}
                  </Button>
                </WrapItem>
              ))}
            </Wrap>

            {/* Metadata badges */}
            <HStack spacing={3} flexWrap="wrap">
              {sim && (
                <>
                  <Badge colorScheme="purple" fontSize="xs" px={2} py={1} borderRadius="md">
                    {sim.ndim}D
                  </Badge>
                  <Tooltip label={`Grid: ${sim.dims.join(' × ')}`}>
                    <Badge colorScheme="gray" fontSize="xs" px={2} py={1} borderRadius="md" fontFamily="mono">
                      {sim.dims.join('×')}
                    </Badge>
                  </Tooltip>
                  {sim.domain && (
                    <Tooltip label={`x: [${sim.domain.x.join(', ')}]  y: [${sim.domain.y.join(', ')}]${sim.ndim === 3 ? `  z: [${sim.domain.z.join(', ')}]` : ''}`}>
                      <Badge colorScheme="blue" fontSize="xs" px={2} py={1} borderRadius="md" cursor="help">
                        Domain
                      </Badge>
                    </Tooltip>
                  )}
                </>
              )}
              {fieldInfo && (
                <Tooltip label={`Range: ${fieldInfo.min.toExponential(3)} → ${fieldInfo.max.toExponential(3)}`}>
                  <Badge colorScheme="teal" fontSize="xs" px={2} py={1} borderRadius="md" fontFamily="mono" cursor="help">
                    {selectedField}: [{fieldInfo.min.toExponential(2)}, {fieldInfo.max.toExponential(2)}]
                  </Badge>
                </Tooltip>
              )}
              {manifest.colormap && (
                <Badge colorScheme="orange" fontSize="xs" px={2} py={1} borderRadius="md">
                  {manifest.colormap}
                </Badge>
              )}
            </HStack>
          </Flex>

          {/* Image Viewport */}
          <Box
            ref={containerRef}
            bg="#0a0a0a"
            borderRadius="xl"
            overflow="hidden"
            position="relative"
            minH="400px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            shadow="lg"
          >
            {!hasDataForCurrentFrame ? (
              <VStack spacing={3}>
                <Text color="gray.500" fontSize="sm">No data for field "{selectedField}" at this timestep</Text>
              </VStack>
            ) : currentImageUrl ? (
              <img
                src={currentImageUrl}
                alt={`${selectedField} step ${currentStep}`}
                style={{
                  width: '100%',
                  height: 'auto',
                  display: 'block',
                }}
              />
            ) : (
              <VStack spacing={3}>
                <Spinner size="lg" color="whiteAlpha.600" thickness="3px" />
                <Text color="gray.500" fontSize="xs">Loading...</Text>
              </VStack>
            )}

            {/* Buffering overlay */}
            {buffering && (
              <Box
                position="absolute"
                top={0} left={0} right={0} bottom={0}
                bg="blackAlpha.700"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <VStack spacing={2}>
                  <Spinner size="lg" color="white" thickness="3px" />
                  <Text color="white" fontSize="sm" fontWeight="medium">Buffering...</Text>
                </VStack>
              </Box>
            )}
          </Box>

          {/* Timeline Controls */}
          {!singleFrame && (
            <Box bg="white" borderRadius="lg" border="1px" borderColor="gray.200" p={4} shadow="sm">
              <VStack spacing={3}>

                {/* Slider row */}
                <HStack spacing={4} w="full">
                  <IconButton
                    icon={playing ? <FaPause /> : <FaPlay />}
                    size="sm"
                    colorScheme="brand"
                    variant={playing ? 'solid' : 'outline'}
                    onClick={() => setPlaying(p => !p)}
                    aria-label={playing ? 'Pause' : 'Play'}
                  />

                  <IconButton
                    icon={<FaStepBackward />}
                    size="xs"
                    variant="ghost"
                    onClick={() => { setPlaying(false); setCurrentIndex(prev => Math.max(prev - 1, 0)); }}
                    isDisabled={currentIndex === 0}
                    aria-label="Previous frame"
                  />

                  <Slider
                    flex={1}
                    min={0}
                    max={totalSteps - 1}
                    value={currentIndex}
                    onChange={(val) => { setPlaying(false); setCurrentIndex(val); }}
                    focusThumbOnChange={false}
                  >
                    <SliderTrack bg="gray.200" h="6px" borderRadius="full">
                      <SliderFilledTrack bg="brand.500" />
                    </SliderTrack>
                    <SliderThumb boxSize={4} bg="brand.500" border="2px solid white" shadow="md" />
                  </Slider>

                  <IconButton
                    icon={<FaStepForward />}
                    size="xs"
                    variant="ghost"
                    onClick={() => { setPlaying(false); setCurrentIndex(prev => Math.min(prev + 1, totalSteps - 1)); }}
                    isDisabled={currentIndex === totalSteps - 1}
                    aria-label="Next frame"
                  />
                </HStack>

                {/* Info row */}
                <Flex
                  direction={{ base: 'column', sm: 'row' }}
                  justify="space-between"
                  align={{ base: 'stretch', sm: 'center' }}
                  w="full"
                  gap={2}
                >
                  <Text fontSize="xs" color="gray.600" fontFamily="mono">
                    step {currentStep.toLocaleString()} / {maxStep.toLocaleString()}
                    <Text as="span" color="gray.400" ml={2}>
                      (frame {currentIndex + 1} / {totalSteps})
                    </Text>
                  </Text>

                  {/* FPS selector */}
                  <HStack spacing={1}>
                    <Text fontSize="xs" color="gray.500" mr={1}>FPS:</Text>
                    {FPS_OPTIONS.map(f => (
                      <Button
                        key={f}
                        size="xs"
                        variant={fps === f ? 'solid' : 'ghost'}
                        colorScheme={fps === f ? 'brand' : 'gray'}
                        onClick={() => setFps(f)}
                        fontFamily="mono"
                        fontSize="xs"
                        minW="32px"
                      >
                        {f}
                      </Button>
                    ))}
                  </HStack>
                </Flex>

                {/* Keyboard hints */}
                <HStack spacing={4} w="full" justify="center">
                  <Text fontSize="2xs" color="gray.400">
                    Space: play/pause
                  </Text>
                  <Text fontSize="2xs" color="gray.400">
                    ← →: step
                  </Text>
                </HStack>
              </VStack>
            </Box>
          )}

        </VStack>
      </Container>

      <Footer />
    </Box>
  );
};

const HeaderBar = ({ user, logout, navigate }) => (
  <Box bg="white" shadow="sm" borderBottom="1px" borderColor="gray.200" position="sticky" top={0} zIndex={10}>
    <Container maxW="container.xl" py={4}>
      <HStack justify="space-between">
        <HStack
          spacing={1}
          cursor="pointer"
          onClick={() => navigate('/')}
          _hover={{ opacity: 0.8 }}
          transition="opacity 0.2s"
          borderRadius="md"
          p={2}
          _active={{ transform: 'scale(0.98)' }}
        >
          <Image src="/simple-logo.png" alt="SRS Logo" boxSize={14} objectFit="contain" />
          <Box>
            <Heading size="lg" color="brand.500">SRS</Heading>
            <Text fontSize="sm" color="gray.600">Scale-Resolving Simulation at Scale</Text>
          </Box>
        </HStack>
        <HStack spacing={4}>
          <NavDropdowns />
          {user ? (
            <Button leftIcon={<FaCloud />} colorScheme="brand" size="sm" variant="ghost" as={RouterLink} to="/dashboard">
              Dashboard
            </Button>
          ) : null}
          {user && (
            <Button leftIcon={<FaSignOutAlt />} variant="outline" size="sm" onClick={() => logout()}>
              Sign Out
            </Button>
          )}
        </HStack>
      </HStack>
    </Container>
  </Box>
);

export default SimulationViewer;
